import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FaHeart, FaComment, FaShare, FaPaperPlane } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PropertyCard from '../../Components/PropertyCard/PropertyCard';
import api from '../../api/axiosConfig';
import { useTranslation } from 'react-i18next';
import { useTranslation as useI18nextTranslation } from 'react-i18next'; 

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-200"></div>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent absolute top-0 left-0"></div>
      </div>
    </div>
  );
}

const OptimizedImage = React.memo(({ src, alt, className, loading = "lazy", t }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);
  const handleImageError = useCallback(() => setImageError(true), []);

  if (imageError) {
    return (
      <div className={`bg-gray-200 dark:bg-gray-700 flex items-center justify-center ${className}`}>
        <span className="text-gray-400">{t('home.imageError')}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        loading={loading}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
});

function CurrencySelector({ userCurrency, setUserCurrency, fxRates, localStorage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(userCurrency);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedCurrency(userCurrency);
  }, [userCurrency]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (currency) => {
    setSelectedCurrency(currency);
    setUserCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-[var(--bg-black)] text-[var(--text-black)] border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[80px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCurrency || 'Currency'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {Object.keys(fxRates).map((currency) => (
            <div
              key={currency}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedCurrency === currency ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(currency)}
            >
              {currency}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LanguageSelector({ i18n, localStorage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  const dropdownRef = useRef(null);
  
  const languages = [
    { code: "kn", name: "Kinyarwanda" },
    { code: "sw", name: "Swahili" },
    { code: "en", name: "English" },
    { code: "lg", name: "Luganda" },
    { code: "zu", name: "Zulu" },
    { code: "fr", name: "Français" },
    { code: "ar", name: "العربية" },
    { code: "hi", name: "हिन्दी" },
    { code: "zh-Hant", name: "中文 (Traditional)" },
    { code: "zh-Hans", name: "中文 (Simplified)" },
    { code: "ak", name: "Akan" },
    { code: "am", name: "Amharic" },
    { code: "ay", name: "Aymara" },
    { code: "bm", name: "Bambara" },
    { code: "be", name: "Bemba" },
    { code: "bn", name: "Bengali" },
    { code: "ber", name: "Berber" },
    { code: "nl", name: "Dutch" },
    { code: "fi", name: "Finnish" },
    { code: "ha", name: "Hausa" },
    { code: "ig", name: "Igbo" },
    { code: "id", name: "Indonesian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "km", name: "Khmer" },
    { code: "ki", name: "Kikuyu" },
    { code: "ko", name: "Korean" },
    { code: "ht", name: "Haitian Creole" },
    { code: "mg", name: "Malagasy" },
    { code: "ml", name: "Malayalam" },
    { code: "mr", name: "Marathi" },
    { code: "ne", name: "Nepali" },
    { code: "om", name: "Oromo" },
    { code: "ps", name: "Pashto" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "pa", name: "Punjabi" },
    { code: "qu", name: "Quechua" },
    { code: "ru", name: "Russian" },
    { code: "so", name: "Somali" },
    { code: "sq", name: "Albanian" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "th", name: "Thai" },
    { code: "ti", name: "Tigrinya" },
    { code: "tr", name: "Turkish" },
    { code: "ur", name: "Urdu" },
    { code: "vi", name: "Vietnamese" },
    { code: "wo", name: "Wolof" },
    { code: "xh", name: "Xhosa" },
    { code: "yo", name: "Yoruba" }
  ];

  const selectedLanguage = languages.find(lang => lang.code === selectedLang);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code, name) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-pink-500 text-white border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[100px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedLanguage?.name || 'Language'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {languages.map((lang) => (
            <div
              key={lang.code}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedLang === lang.code ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(lang.code, lang.name)}
            >
              {lang.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const scrollTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation('Homedash');
  const { i18n } = useI18nextTranslation(); 
  
  const [userCurrency, setUserCurrency] = useState('RWF');
  const [fxRates, setFxRates] = useState({});

  useEffect(() => {
    let isCancelled = false;
    const fetchProperties = async () => {
      try {
        const response = await api.get('/properties/unique-users/');
        const data = response.data.results || response.data;
        if (!isCancelled) {
          setProperties(Array.isArray(data) ? data.slice(0, 10) : []);
        }
      } catch (err) {
        console.error('Failed to load properties:', err);
        if (!isCancelled) {
          setProperties([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    fetchProperties();
    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchFxRates = async () => {
      try {
        const fxRes = await api.get('/fx-rates/');
        setFxRates(fxRes.data);
        const savedCurrency = localStorage.getItem('preferredCurrency') || 'RWF';
        setUserCurrency(savedCurrency);
      } catch (err) {
        console.error('Failed to fetch FX rates:', err);
      }
    };
    fetchFxRates();
  }, []);

  const handleScroll = useCallback((e) => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    setIsScrolling(true);
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 150);
    const container = e.currentTarget;
    const scrollPosition = container.scrollTop;
    const itemHeight = window.innerHeight;
    const newIndex = Math.round(scrollPosition / itemHeight);
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < properties.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, properties.length]);

  const handleImageLoad = useCallback((propertyId) => {
    setLoadedImages(prev => new Set(prev).add(propertyId));
  }, []);

  // Preload adjacent images
  useEffect(() => {
    const preloadImages = () => {
      const indicesToPreload = [
        Math.max(0, currentIndex - 1),
        currentIndex,
        Math.min(properties.length - 1, currentIndex + 1),
        Math.min(properties.length - 1, currentIndex + 2)
      ];
      
      indicesToPreload.forEach(index => {
        const property = properties[index];
        if (property && !loadedImages.has(property.id)) {
          const img = new Image();
          img.src = property.images[0]?.image || '/fallback.jpg';
          img.onload = () => handleImageLoad(property.id);
        }
      });
    };
    
    if (properties.length > 0) preloadImages();
  }, [currentIndex, loadedImages, properties, handleImageLoad]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
    }
  }, [handleScroll]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp' && currentIndex > 0) {
        containerRef.current?.scrollTo({ 
          top: (currentIndex - 1) * window.innerHeight, 
          behavior: 'smooth' 
        });
      } else if (e.key === 'ArrowDown' && currentIndex < properties.length - 1) {
        containerRef.current?.scrollTo({ 
          top: (currentIndex + 1) * window.innerHeight, 
          behavior: 'smooth' 
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, properties.length]);

  const redirectToLogin = (e) => {
    e.stopPropagation();
    navigate('/login');
  };



  if (loading) return <LoadingSpinner />;
  if (properties.length === 0) return <div className="text-white text-center py-20">{t('home.noProperties')}</div>;

  return (
    <div className="flex h-screen bg-[var(--bg-black)] overflow-hidden">
      <div className="flex-1 transition-all duration-300">
        <div
          ref={containerRef}
          className="h-screen pt-14 sm:pt-16 overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
          style={{ 
            scrollSnapType: 'y mandatory', 
            scrollBehavior: 'smooth' 
          }}
        >
          {properties.map((property, index) => (
            <div
              key={property.id}
              className="h-screen snap-start relative flex items-center justify-center px-2 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8 lg:py-10"
              style={{ scrollSnapAlign: 'start' }}
            >
              <div className="relative flex items-center justify-center w-full max-w-6xl h-full">
                <PropertyCard
                  property={property}
                  isActive={index === currentIndex}
                  isScrolling={isScrolling}
                  loading={index <= 1 ? "eager" : "lazy"}
                  onImageLoad={handleImageLoad}
                  fxRates={fxRates}
                  userCurrency={userCurrency}
                />
                
                <div className="absolute right-2 sm:right-4 md:right-6 lg:right-12 bottom-19 sm:bottom-20 flex flex-col gap-4 sm:gap-6 z-10">
                  <div className="flex flex-col rounded-full items-center sm:mr-6 md:mr-16 lg:mr-16">
                    <div className="relative">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 sm:border-3 border-white shadow-lg">
                        <OptimizedImage
                          src={property.owner.avatar || `https://ui-avatars.com/api/?name=${(property.owner.username || 'U')[0].toUpperCase()}&background=ff6b6b&color=fff&size=128`}
                          alt={property.owner.username}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          t={t}
                        />
                      </div>
                      <button 
                        className="absolute border border-none -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 sm:w-7 sm:h-7 bg-pink-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold hover:bg-pink-600 transition-all duration-200 hover:scale-110"
                        onClick={redirectToLogin}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-4 -mb-10 sm:mr-6 md:mr-16 lg:mr-16">
                    {/* Like */}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={redirectToLogin}
                        className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                        aria-label="Like"
                      >
                        <FaHeart className="text-[var(--text-black)]" size={18} />
                      </button>
                      <span className="text-[var(--text-black)] text-xs font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {t('home.actions.like')}
                      </span>
                      {property.likes_count > 0 && (
                        <span className="text-[var(--text-black)] text-xs font-medium mt-1">
                          {property.likes_count}
                        </span>
                      )}
                    </div>
                    
                    {/* Comment */}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={redirectToLogin}
                        className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                        aria-label="Comment"
                      >
                        <FaComment className="text-[var(--text-black)]" size={16} />
                      </button>
                      <span className="text-[var(--text-black)] text-xs font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {t('home.actions.comment')}
                      </span>
                      {property.comments_count > 0 && (
                        <span className="text-[var(--text-black)] text-xs font-medium mt-1">
                          {property.comments_count}
                        </span>
                      )}
                    </div>
                    
                    {/* Share */}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={redirectToLogin}
                        className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                        aria-label="Share"
                      >
                        <FaShare className="text-[var(--text-black)]" size={16} />
                      </button>
                      <span className="text-[var(--text-black)] text-xs font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {t('home.actions.share')}
                      </span>
                      {property.shares_count > 0 && (
                        <span className="text-[var(--text-black)] text-xs font-medium mt-1">
                          {property.shares_count}
                        </span>
                      )}
                    </div>
                    
                    {/* Message (replaces Save) */}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={redirectToLogin}
                        className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                        aria-label="Message"
                      >
                        <FaPaperPlane className="text-[var(--text-black)]" size={16} />
                      </button>
                      <span className="text-[var(--text-black)] text-xs font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {t('home.actions.message')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
      
            </div>
          ))}
        </div>
      </div>
      
      <div className="fixed top-4 right-4 z-40 flex gap-2">
        <CurrencySelector 
          userCurrency={userCurrency} 
          setUserCurrency={setUserCurrency} 
          fxRates={fxRates} 
          localStorage={localStorage} 
        />
        
        <LanguageSelector 
          i18n={i18n} 
          localStorage={localStorage} 
        />
      </div>

      
      
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        @media (prefers-reduced-motion: reduce) {
          * { 
            animation-duration: 0.01ms !important; 
            transition-duration: 0.01ms !important; 
          }
          .snap-y { 
            scroll-behavior: auto; 
          }
        }

      `}</style>
    </div>
  );
}